import { inject, Injectable } from '@angular/core';
import { ApplicationAuthServiceInterface } from '@models/authentication';
import { GraphQLClientNames } from '@models/GraphQL';
import { TwinUser } from '@shared/authentication';
import { Apollo } from 'apollo-angular';
import jwtDecode from 'jwt-decode';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { filter, from, map, Observable, of, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class KeycloakAuthService implements ApplicationAuthServiceInterface {
  twinUser$: Observable<TwinUser> = of(null);
  isLoggedIn = false;

  private readonly keycloak = inject(Keycloak);
  private readonly apolloClient = inject(Apollo);

  init(): Observable<string> {
    return of('KeycloakAuthService: initialize...');
  }

  getUserProfile(): Observable<TwinUser> {
    return from(of(!!this.keycloak.authenticated)).pipe(
      tap(isLoggedIn => (this.isLoggedIn = isLoggedIn)),
      filter(isLoggedIn => isLoggedIn),
      switchMap(() => from(this.keycloak.loadUserProfile())),
      map(keycloakProfile => this.buildProfile(keycloakProfile))
    );
  }

  buildProfile(keycloakProfile: KeycloakProfile): TwinUser {
    const displayName = keycloakProfile.firstName + ' ' + keycloakProfile.lastName;
    return {
      applicationDisplayName: displayName,
      email: keycloakProfile.email,
      displayName,
      avatar: '',
      defaultTenant: 'T101014843',
      currentTenant: 'T101014843',
      tenants: [
        {
          id: 'T101014843',
          name: 'Keycloak Test'
        },
        {
          id: 'T101014844',
          name: 'Aize Test tenant'
        }
      ]
    };
  }

  decodeToken(token: string): string {
    return jwtDecode(token);
  }

  async logout(): Promise<void> {
    try {
      await Promise.all([
        this.apolloClient.use(GraphQLClientNames.Core).client.clearStore(),
        this.apolloClient.use(GraphQLClientNames.Linear).client.clearStore()
      ]);
    } finally {
      await this.keycloak.logout();
    }
  }

  login(): Observable<string> {
    return from(this.keycloak.login().then(() => 'KeycloakAuthService: login...'));
  }
}
